import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CreateGeofenceRequest, Geofence, UpdateGeofenceRequest } from 'src/app/models/geofence';
import { GeofenceSortOrder, PageRequest } from 'src/app/models/page-request';
import { stripUndefined } from 'src/app/shared/utils';
import { PageResponse } from 'src/app/models/page-response';
import { UserService } from './user.service';
import { ComponentType } from 'src/app/models/component-type';

@Injectable({
  providedIn: 'root',
})
export class GeofenceService {
  private readonly baseUrl = `${environment.api}/api/${this.userService.getOrganisationId()}/geofence`;

  // BehaviorSubject to track geofences state
  private geofencesSubject = new BehaviorSubject<Geofence[]>([]);
  public geofences$ = this.geofencesSubject.asObservable();

  constructor(private http: HttpClient, private userService: UserService) {}

  getGeofences(pageRequest: PageRequest<GeofenceSortOrder>): Observable<PageResponse<Geofence>> {
    return this.http
      .get<PageResponse<Geofence>>(this.baseUrl, {
        params: stripUndefined({
          ...pageRequest,
        }),
      })
      .pipe(
        tap((response) => {
          this.geofencesSubject.next(response.items);
        })
      );
  }

  createGeofence(createGeofenceRequest: CreateGeofenceRequest): Observable<Geofence> {
    return this.http
      .post<Geofence>(
        this.baseUrl,
        stripUndefined({
          ...createGeofenceRequest,
        })
      )
      .pipe(
        tap((newGeofence) => {
          const currentGeofences = this.geofencesSubject.value;
          this.geofencesSubject.next([...currentGeofences, newGeofence]);
        })
      );
  }

  updateGeofence(id: string, updateGeofenceRequest: UpdateGeofenceRequest): Observable<Geofence> {
    return this.http
      .put<Geofence>(
        `${this.baseUrl}/${id}`,
        stripUndefined({
          ...updateGeofenceRequest,
        })
      )
      .pipe(
        tap((updatedGeofence: Geofence) => {
          const currentGeofences = this.geofencesSubject.value;
          const index = currentGeofences.findIndex((g) => g.id === id);
          if (index !== -1) {
            currentGeofences[index] = updatedGeofence;
            this.geofencesSubject.next([...currentGeofences]);
          }
        })
      );
  }

  deleteGeofence(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        const current = this.geofencesSubject.value;
        this.geofencesSubject.next(current.filter((g) => g.id !== id));
      })
    );
  }

  getComponentTypesForGeofence(geofenceId: string): Observable<ComponentType[]> {
    return this.http.get<ComponentType[]>(`${this.baseUrl}/${geofenceId}/componenttype`);
  }
}
